import { ContentPage } from "./ContentPage";
import { PLATFORM } from "../../config/platform";

// Reusable content body — used both on the public page and the in-app
// (Settings → Terms of Service) screen. Keep in sync with the mobile app's
// copy at mobileapp/src/screens/terms/TermsOfService.tsx.
export const TermsBody = () => (
  <>
    <p className="content__meta">Last updated: 4 August 2026</p>

    <p>
      These Terms of Service ("Terms") govern your access to and use of the
      {PLATFORM.platform_name} inventory and business management platform (the "Service"),
      operated by Mangol Group ("we", "us"). By creating an account or using
      the Service, you agree to these Terms. If you are agreeing on behalf of
      a company, you confirm you have authority to bind that company.
    </p>

    <h2>1. Accounts and organizations</h2>
    <ul className="list-disc">
      <li>
        You must provide accurate registration details and keep them current.
      </li>
      <li>
        You are responsible for safeguarding your credentials and for all
        activity under your account. Notify us promptly of any unauthorised
        use.
      </li>
      <li>
        The Service is organised around organizations. An organization owner
        may invite, suspend and remove members, and can access data those
        members create within that organization.
      </li>
      <li>
        Accounts are for named individuals. Do not share a single login
        between multiple people.
      </li>
    </ul>

    <h2>2. Subscriptions, plans and billing</h2>
    <ul className="list-disc">
      <li>
        Access is provided under a subscription plan. Plan limits (such as
        the number of users and products) are shown when you choose or
        request a plan.
      </li>
      <li>
        New organizations may begin on a trial. When a trial ends, access is
        restricted until a subscription is activated.
      </li>
      <li>
        Plan changes are requested in-app and take effect once approved and
        activated by us. Pricing for custom plans is agreed separately.
      </li>
      <li>
        Fees are invoiced and paid outside the mobile applications. No
        payment is collected inside the iOS or Android apps.
      </li>
      <li>
        Unless required by law, fees already paid are non-refundable. We may
        change pricing with reasonable prior notice, effective from your next
        billing period.
      </li>
    </ul>

    <h2>3. Acceptable use</h2>
    <p>You agree not to:</p>
    <ul className="list-disc">
      <li>
        break the law, infringe others' rights, or upload unlawful, harmful
        or infringing content;
      </li>
      <li>
        attempt to access another organization's data, probe or breach
        security, or bypass access controls or plan limits;
      </li>
      <li>
        interfere with or place undue load on the Service, including
        automated scraping or excessive API use;
      </li>
      <li>
        reverse engineer, resell or sublicense the Service except under a
        reseller agreement with us.
      </li>
    </ul>
    <p>
      We may suspend access that threatens the security, integrity or
      availability of the Service, or that breaches these Terms.
    </p>

    <h2>4. Your data</h2>
    <ul className="list-disc">
      <li>
        You retain ownership of the data you and your organization put into
        the Service ("Your Data").
      </li>
      <li>
        You grant us the limited rights needed to host, process, back up and
        display Your Data in order to provide and support the Service.
      </li>
      <li>
        You are responsible for having the necessary rights and consents for
        Your Data, including any personal information about your customers,
        suppliers or staff.
      </li>
      <li>
        Our handling of personal information is described in our{" "}
        <a href="/privacy-policy">Privacy Policy</a>, which forms part of
        these Terms.
      </li>
    </ul>

    <h2>5. Availability and support</h2>
    <p>
      We aim to keep the Service available and reliable, but it is provided on
      an "as is" and "as available" basis. Maintenance, updates and
      circumstances outside our reasonable control may cause interruptions. We
      do not guarantee uninterrupted or error-free operation. Support is
      available at{" "}
      <a href={`mailto:${PLATFORM.support.support_email}`}>
        {PLATFORM.support.support_email}
      </a>
      .
    </p>

    <h2>6. Cancellation, deletion and termination</h2>
    <ul className="list-disc">
      <li>
        You may delete your own account at any time from{" "}
        <strong>Profile → Delete my account</strong> (mobile app:{" "}
        <strong>Settings → Account Settings</strong>). Deletion is scheduled
        with a grace period during which you can restore the account — see{" "}
        <a href="/delete-account">Delete Your Account</a> for full details.
      </li>
      <li>
        If you are the sole owner of an organization that still has other
        members, you must transfer ownership before your account can be
        deleted.
      </li>
      <li>
        We may suspend or terminate access for material breach of these
        Terms, non-payment, or where required by law. Where practical we will
        give notice and an opportunity to remedy.
      </li>
      <li>
        Following termination we may retain records for a period as described
        in our Privacy Policy, including where retention is required for
        legal, security or accounting purposes.
      </li>
    </ul>

    <h2>7. Intellectual property</h2>
    <p>
      The Service, including its software, design and branding, remains our
      property and that of our licensors. These Terms grant you a limited,
      non-exclusive, non-transferable right to use the Service for your
      internal business purposes for the duration of your subscription. No
      other rights are granted.
    </p>

    <h2>8. Third-party services</h2>
    <p>
      The Service relies on third-party infrastructure and may link to
      third-party services. We are not responsible for those services, and
      your use of them is governed by their own terms.
    </p>

    <h2>9. Disclaimers and limitation of liability</h2>
    <p>
      To the fullest extent permitted by law, we exclude all implied
      warranties, including fitness for a particular purpose and
      merchantability. The Service supports your record-keeping but does not
      constitute accounting, tax or legal advice — you remain responsible for
      verifying your records and meeting your own compliance obligations.
    </p>
    <p>
      To the fullest extent permitted by law, we are not liable for indirect,
      incidental, special or consequential losses, or for lost profits,
      revenue, goodwill or data. Our total aggregate liability arising out of
      or relating to the Service is limited to the fees you paid to us for the
      Service in the twelve months preceding the event giving rise to the
      claim. Nothing in these Terms limits liability that cannot be limited
      under applicable law.
    </p>

    <h2>10. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. Where changes are material
      we will give reasonable notice, for example by email or an in-app
      notice. Continuing to use the Service after the changes take effect
      means you accept the updated Terms.
    </p>

    <h2>11. Governing law</h2>
    <p>
      These Terms are governed by the laws of India, and the courts of
      Kolkata, West Bengal have exclusive jurisdiction over any dispute,
      without prejudice to any mandatory consumer protections available to you
      where you live.
    </p>

    <h2>12. Contact us</h2>
    <p>
      Questions about these Terms? Email{" "}
      <a href={`mailto:${PLATFORM.support.support_email}`}>
        {PLATFORM.support.support_email}
      </a>{" "}
      or see our <a href="/contact-us">Contact page</a>.
    </p>
  </>
);

export const Terms = ({ onBack }: { onBack: () => void }) => (
  <ContentPage title="Terms of Service" onBack={onBack}>
    <TermsBody />
  </ContentPage>
);

export default Terms;
